import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import "styles/ui/BSLLoader.css";
import { useTenant } from "config/tenant";

const BSLLoader = ({
  show = false,
  overlay = true,
  fullPageOverlay = false, // true = toute la page, false = conteneur parent seulement
  minHeight = false, // true = min-height 100vh pour garder le centrage
  size = "lg", // sm, md, lg
  className = "",
  children = null,
  minDuration = 600, // Durée minimale d'affichage en ms (600ms par défaut)
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [isTall, setIsTall] = useState(false);
  const overlayRef = useRef(null);
  const { shortName: tenantShortName } = useTenant();

  useEffect(() => {
    if (show && !isVisible) {
      setIsVisible(true);
      setStartTime(Date.now());
    } else if (!show && isVisible && startTime) {
      const elapsed = Date.now() - startTime;
      const remaining = minDuration - elapsed;

      if (remaining > 0) {
        const timer = setTimeout(() => {
          setIsVisible(false);
          setStartTime(null);
        }, remaining);

        return () => clearTimeout(timer);
      }
      setIsVisible(false);
      setStartTime(null);
    }
  }, [show, isVisible, startTime, minDuration]);

  // Détecter si le conteneur parent est plus haut que la viewport
  useEffect(() => {
    if (minHeight && isVisible && overlayRef.current) {
      const checkHeight = () => {
        const parentElement = overlayRef.current.parentElement;
        if (parentElement) {
          const containerHeight = parentElement.scrollHeight;
          const viewportHeight = window.innerHeight;
          setIsTall(containerHeight > viewportHeight * 1.2);
        }
      };

      checkHeight();
      const timer = setTimeout(checkHeight, 100);

      window.addEventListener("resize", checkHeight);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", checkHeight);
      };
    }
  }, [minHeight, isVisible]);

  const Loader = () => (
    <div className={`bsl-loader bsl-loader-${size} ${className}`.trim()}>
      <div className="ring"></div>
      <div className="ring ring-2"></div>
      {/* Sigle de la societe servie, et non « BSL » en dur : ce loader
          s'affiche a chaque chargement de tableau, c'etait la marque la plus
          visible du frontend. Vide tant que l'identite n'est pas connue —
          les anneaux tournent quand meme. Le texte est centre par flex sans
          largeur figee, un sigle plus long qu'« BSL » ne casse rien. */}
      <span>{tenantShortName}</span>
    </div>
  );

  if (!overlay) {
    if (!isVisible) return children ?? null;
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "32px 0",
        }}
      >
        <Loader />
      </div>
    );
  }

  let overlayClass;
  if (fullPageOverlay) {
    overlayClass = "loader-overlay-fullpage";
  } else if (minHeight) {
    overlayClass = "loader-overlay-min-height";
    if (isTall) {
      overlayClass += " is-tall";
    }
  } else {
    overlayClass = "loader-overlay";
  }

  const attachRef = minHeight && !fullPageOverlay;

  return (
    <div
      className="bsl-loader-host"
      style={{ position: "relative", width: "100%" }}
      aria-busy={isVisible}
    >
      {isVisible && (
        <div
          className={overlayClass}
          ref={attachRef ? overlayRef : undefined}
        >
          <Loader />
        </div>
      )}
      {children}
    </div>
  );
};

BSLLoader.propTypes = {
  show: PropTypes.bool,
  overlay: PropTypes.bool,
  fullPageOverlay: PropTypes.bool,
  minHeight: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
  children: PropTypes.node,
  minDuration: PropTypes.number,
};

export default BSLLoader;
